import {
  Alert, AlertTitle, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import React, { useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import PropTypes from 'prop-types';
import ControlledTable from '../../../shared/ui/ControlledTable/ControlledTable';
import FormDialog from '../../../shared/ui/FormDialog/FormDialog';
import Loading from '../../../shared/ui/Loading/Loading';
import { useAppDispatch } from '../../../app/store';
import { showNotification } from '../../../shared/snackbar/redux/snackbar.slice';

const headCells = [
  { id: 'name', numeric: false, disablePadding: false, label: 'Название' },
  { id: 'token', numeric: false, disablePadding: false, label: 'Токен' },
  { id: 'expiresAt', numeric: false, disablePadding: false, label: 'Истекает' },
];

const displayedCells = ['name', 'token', 'expiresAt'];

function formatDate(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return d.toLocaleDateString('ru-RU', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}
/**
 * Показ и работа со списком токенов текущего пользователя.
 */
function ApiTokensPanel({
  data,
  status,
  createApiToken,
  deleteApiToken,
}) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newTokenResult, setNewTokenResult] = useState(null);
  const confirm = useConfirm();
  const dispatch = useAppDispatch();

  const rows = (data || []).map((item) => ({
    id: item.apiTokenId || item.id,
    name: item.name || '-',
    token: item.token || '-',
    expiresAt: formatDate(item.expirationDate),
  }));

  const handleCreate = async (name) => {
    if (!name || !name.trim()) {
      dispatch(showNotification({ message: 'Введите название токена', severity: 'warning' }));
      return;
    }
    try {
      const result = await createApiToken({ name: name.trim() }).unwrap();
      setNewTokenResult(result);
      dispatch(showNotification({ message: 'Токен успешно создан', severity: 'success' }));
    } catch {
      dispatch(showNotification({ message: 'Ошибка при создании токена', severity: 'error' }));
    }
  };

  const handleDelete = async (row) => {
    try {
      await confirm({
        title: 'Удаление токена',
        confirmationText: 'Да',
        cancellationText: 'Отмена',
        description: `Вы действительно хотите удалить токен «${row.name}»?`,
      });
      await deleteApiToken({ id: row.id }).unwrap();
      dispatch(showNotification({ message: 'Токен удалён', severity: 'success' }));
    } catch (e) {
      if (e !== undefined) {
        dispatch(showNotification({ message: 'Ошибка при удалении токена', severity: 'error' }));
      }
    }
  };

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token).then(() => {
      dispatch(showNotification({ message: 'Токен скопирован', severity: 'success' }));
    });
  };

  if (status === 'pending') {
    return <Loading />;
  }

return (
    <Box>
      {status === 'rejected' ? (
        <Alert severity="error">
          <AlertTitle>Ошибка</AlertTitle>
          Ошибка при загрузке токенов
        </Alert>
      ) : null}

      <ControlledTable
        rows={rows}
        headCells={headCells}
        displayedCells={displayedCells}
        countRowsPerPage={10}
        selected={[]}
        setSelected={() => {}}
        controls
        onDelete={(row) => handleDelete(row)}
        disableCheckbox
        hover
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<VpnKeyIcon />}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Создать
        </Button>
      </Box>

      <FormDialog
        open={isCreateDialogOpen}
        handleClose={() => setIsCreateDialogOpen(false)}
        onSave={handleCreate}
        label="Название токена"
        helperText="Введите понятное название для нового токена"
        title="Создание API токена"
        saveTitle="Создать"
        cancelTitle="Отмена"
      />

      <FormDialog
        open={isCreateDialogOpen}
        handleClose={() => setIsCreateDialogOpen(false)}
        onSave={handleCreate}
        label="Название токена"
        helperText="Введите понятное название для нового токена"
        title="Создание API токена"
        saveTitle="Создать"
        cancelTitle="Отмена"
      />

      <Dialog
        open={!!newTokenResult}
        onClose={() => {
          setNewTokenResult(null);
          setIsCreateDialogOpen(false);
        }}
        fullWidth
      >
        <DialogTitle>API токен создан</DialogTitle>
        <DialogContent>
          {newTokenResult ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Токен успешно создан</AlertTitle>
                Скопируйте токен сейчас. Вы не сможете увидеть его снова.
              </Alert>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  wordBreak: 'break-all',
                }}
              >
                {newTokenResult.token}
                <Tooltip title="Копировать">
                  <IconButton size="small" onClick={() => handleCopyToken(newTokenResult.token)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewTokenResult(null);
            setIsCreateDialogOpen(false);
          }}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

ApiTokensPanel.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    token: PropTypes.string,
    expiresAt: PropTypes.string,
  })),
  status: PropTypes.string,
  createApiToken: PropTypes.func.isRequired,
  deleteApiToken: PropTypes.func.isRequired,
};

ApiTokensPanel.defaultProps = {
  data: null,
  status: null,
};

export default ApiTokensPanel;